import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { ConstantineProgressPanel } from '@/sections/poe/components/ConstantineProgressPanel';
import type { Attribute } from '@/types';

const FLOW_LABELS: Record<string, string> = {
  exploit: 'Exploit',
  patch: 'Patch',
  patch_and_pr: 'Patch & PR',
  validate: 'Validate Patch',
};

const ATTR_PREFIX = 'constantine-progress-';
const POLL_INTERVAL_MS = 5_000;

interface FlowEntry {
  flow: string;
  conversationId: string;
}

interface ConstantineDemonstratedSectionProps {
  riskKey: string;
  attributes?: Array<{ name: string; value: string }>;
}

/** Extract flow entries from attributes matching constantine-progress-* */
function extractFlows(
  attrs: Array<{ name: string; value: string }> | undefined
): FlowEntry[] {
  if (!attrs) return [];
  const entries: FlowEntry[] = [];
  for (const attr of attrs) {
    if (attr.name.startsWith(ATTR_PREFIX) && attr.value) {
      entries.push({
        flow: attr.name.slice(ATTR_PREFIX.length),
        conversationId: attr.value,
      });
    }
  }
  return entries;
}

export function ConstantineDemonstratedSection({
  riskKey,
  attributes,
}: ConstantineDemonstratedSectionProps) {
  const axios = useAxios();

  // Flows derived from prop attributes (initial source of truth)
  const propFlows = extractFlows(attributes);

  // Local state for flows discovered via polling (merged with prop flows)
  const [polledFlows, setPolledFlows] = useState<FlowEntry[]>([]);

  // Merge prop flows and polled flows, deduplicating by flow type
  const flows = (() => {
    const seen = new Map<string, FlowEntry>();
    for (const entry of propFlows) {
      seen.set(entry.flow, entry);
    }
    for (const entry of polledFlows) {
      if (!seen.has(entry.flow)) {
        seen.set(entry.flow, entry);
      }
    }
    return Array.from(seen.values());
  })();

  const [expandedFlows, setExpandedFlows] = useState<Set<string>>(
    () =>
      new Set(flows.length > 0 ? [flows[flows.length - 1].conversationId] : [])
  );

  // Auto-expand newly discovered flows (setState-during-render prev-value
  // pattern — see react.dev "Storing information from previous renders")
  const [prevFlowCount, setPrevFlowCount] = useState(flows.length);
  if (flows.length !== prevFlowCount) {
    setPrevFlowCount(flows.length);
    if (flows.length > prevFlowCount && flows.length > 0) {
      setExpandedFlows(prevSet => {
        const next = new Set(prevSet);
        next.add(flows[flows.length - 1].conversationId);
        return next;
      });
    }
  }

  // Poll /my for new constantine-progress-* attributes
  useEffect(() => {
    if (!riskKey) return;

    const pollForAttributes = async () => {
      try {
        // The riskKey is in the form "#risk#<dns>#<name>" — the attribute source
        // key is "source:#risk#<dns>#<name>" to match the backend convention.
        const response = await axios.get('/my', {
          params: { label: 'attribute', key: `source:${riskKey}` },
        });
        const attrs = (response.data.attributes || []) as Attribute[];
        const discovered = extractFlows(attrs);
        setPolledFlows(prev => {
          // Only update state if entries actually changed
          if (
            prev.length === discovered.length &&
            prev.every(
              (p, i) =>
                p.flow === discovered[i].flow &&
                p.conversationId === discovered[i].conversationId
            )
          ) {
            return prev;
          }
          return discovered;
        });
      } catch {
        // Ignore poll failures — next tick retries
      }
    };

    void pollForAttributes();
    const interval = setInterval(pollForAttributes, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [riskKey, axios]);

  const toggleFlow = (flow: string) => {
    setExpandedFlows(prev => {
      const next = new Set(prev);
      if (next.has(flow)) {
        next.delete(flow);
      } else {
        next.add(flow);
      }
      return next;
    });
  };

  if (flows.length === 0) {
    return (
      <div className="text-chariot-text-secondary text-sm">
        <p className="text-chariot-text-primary font-medium">
          No agent activity
        </p>
        <p className="mt-1">
          Trigger an exploit, patch, or validation to see live agent progress
          here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {flows.map((entry: FlowEntry) => {
        const isExpanded = expandedFlows.has(entry.conversationId);
        const label = FLOW_LABELS[entry.flow] || entry.flow;

        return (
          <div
            key={entry.conversationId}
            className="border-chariot-stroke overflow-hidden rounded-lg border"
          >
            <button
              onClick={() => toggleFlow(entry.conversationId)}
              className="hover:bg-chariot-background-secondary/50 flex w-full items-center gap-2 px-4 py-2.5"
            >
              {isExpanded ? (
                <ChevronDownIcon className="text-chariot-text-secondary size-3.5 shrink-0" />
              ) : (
                <ChevronRightIcon className="text-chariot-text-secondary size-3.5 shrink-0" />
              )}
              <span className="text-chariot-text-primary text-sm font-medium">
                {label}
              </span>
            </button>

            {isExpanded && (
              <div className="border-chariot-stroke border-t">
                <ConstantineProgressPanel
                  conversationId={entry.conversationId}
                  title={label}
                  hideHeader
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
